import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { useInfiniteDataSourcesQuery, useBulkReorderDataSourcesMutation } from '@/hooks/useDataSources'
import { useConnectionStore } from '@/stores/connectionStore'
import { useSearchStore } from '@/stores/searchStore'
import { ConnectionCard } from '@/components/connections/ConnectionCard'
import { ConnectionModal } from '@/components/connections/ConnectionModal'
import { PlusIcon, ArrowsUpDownIcon, MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline'
import type { DataSource } from '@/services/api'
import { toast } from 'react-hot-toast'
import { clsx } from 'clsx'
import Fuse from 'fuse.js'

export function ConnectionsPage() {
  const { 
    data,
    isLoading, 
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch
  } = useInfiniteDataSourcesQuery(12) // 12 items per page for better grid layout
  const { activeConnection } = useConnectionStore()
  const { connectionsSearch, setConnectionsSearch, clearConnectionsSearch } = useSearchStore()
  const [showModal, setShowModal] = useState(false)
  const [editingConnection, setEditingConnection] = useState<DataSource | null>(null)
  const [draggedItem, setDraggedItem] = useState<DataSource | null>(null)
  const [draggedOverId, setDraggedOverId] = useState<string | null>(null)
  const [isReorderMode, setIsReorderMode] = useState(false)
  const bulkReorderMutation = useBulkReorderDataSourcesMutation()
  const dragCounter = useRef(0)

  // Fuzzy search configuration
  const fuseOptions = useMemo(() => ({
    keys: ['displayName'],
    threshold: 0.4, // 0.0 = exact match, 1.0 = matches anything
    includeScore: true,
    minMatchCharLength: 1,
    ignoreLocation: true,
  }), [])

  // Real-time search functionality with persistent state
  const handleSearchChange = useCallback((query: string) => {
    setConnectionsSearch(query)
  }, [setConnectionsSearch])

  const handleClearSearch = useCallback(() => {
    clearConnectionsSearch()
  }, [clearConnectionsSearch])

  // Get total connections count and filtered connections
  const { totalConnections, connections } = useMemo(() => {
    const allConnections = data?.pages.flatMap(page => page.data) ?? []
    const totalCount = allConnections.length
    
    // Apply fuzzy search if there's a search query
    const filteredConnections = connectionsSearch.trim() 
      ? (() => {
          const fuse = new Fuse(allConnections, fuseOptions)
          const searchResults = fuse.search(connectionsSearch)
          // Extract the original items from Fuse results and sort by relevance score
          return searchResults.map(result => result.item)
        })()
      : allConnections
    
    // Sort by order field if it exists (for non-search results)
    if (!connectionsSearch.trim()) {
      const sortedConnections = filteredConnections.sort((a, b) => {
        if (a.order !== undefined && b.order !== undefined) {
          return a.order - b.order
        }
        return 0
      })
      return { totalConnections: totalCount, connections: sortedConnections }
    }
    
    // For search results, maintain Fuse.js relevance order
    return { totalConnections: totalCount, connections: filteredConnections }
  }, [data, connectionsSearch, fuseOptions])

  // Determine if search should be shown (only show if 2+ datasources)
  const shouldShowSearch = totalConnections >= 2

  // Clear search when search should be hidden
  useEffect(() => {
    if (!shouldShowSearch && connectionsSearch) {
      clearConnectionsSearch()
    }
  }, [shouldShowSearch, connectionsSearch, clearConnectionsSearch])

  // Stable callback functions to prevent re-renders
  const handleReload = useCallback(() => {
    window.location.reload()
  }, [])

  const handleCreateConnection = useCallback(() => {
    setEditingConnection(null)
    setShowModal(true)
  }, [])

  const createEditHandler = useCallback((connection: DataSource) => {
    return () => { handleEditConnection(connection) }
  }, [])

  // Track if initial fetch has been done to prevent duplicates
  const hasFetchedRef = useRef(false)
  
  // Fetch fresh data on mount, avoiding duplicates
  useEffect(() => {
    // Prevent duplicate fetches (React StrictMode, re-renders, etc.)
    if (hasFetchedRef.current) return
    hasFetchedRef.current = true
    
    void refetch()
  }, [refetch]) // Run on mount and when refetch changes

  // Auto-refresh datasources every 5 seconds to keep data fresh (pause when modal is open)
  useEffect(() => {
    if (showModal) {
      return // Don't auto-refresh when editing
    }

    let intervalId: NodeJS.Timeout | undefined

    // Start periodic refresh after initial delay to avoid conflicts with mount fetch
    const timeoutId = setTimeout(() => {
      intervalId = setInterval(() => {
        void refetch()
      }, 5000) // Refresh every 5 seconds
    }, 5000) // Wait 5 seconds before starting periodic refresh

    return () => {
      clearTimeout(timeoutId)
      if (intervalId) clearInterval(intervalId)
    }
  }, [refetch, showModal])

  // Set up infinite scroll using the main container
  useEffect(() => {
    let scrollTimeout: NodeJS.Timeout

    const handleScroll = (event: Event) => {
      // Debounce scroll events to prevent excessive calls
      clearTimeout(scrollTimeout)
      scrollTimeout = setTimeout(() => {
        const {target} = event
        if (!target || !(target instanceof HTMLElement)) return

        const { scrollTop, scrollHeight, clientHeight } = target
        const isNearBottom = scrollTop + clientHeight >= scrollHeight - 300

        if (isNearBottom && hasNextPage && !isFetchingNextPage) {
          console.log('Loading more connections...')
          void fetchNextPage()
        }
      }, 100) // 100ms debounce
    }

    // Find the main scrollable container
    const mainElement = document.querySelector('main')
    if (mainElement) {
      mainElement.addEventListener('scroll', handleScroll, { passive: true })
      return () => {
        mainElement.removeEventListener('scroll', handleScroll)
        clearTimeout(scrollTimeout)
      }
    }
    
    return () => {
      clearTimeout(scrollTimeout)
    }
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const handleEditConnection = (connection: DataSource) => {
    setEditingConnection(connection)
    setShowModal(true)
  }

  const handleCloseModal = useCallback(() => {
    setShowModal(false)
    setEditingConnection(null)
  }, [])

  // Drag and drop handlers
  const handleDragStart = useCallback((e: React.DragEvent<HTMLDivElement>, item: DataSource) => {
    if (!isReorderMode) return
    setDraggedItem(item)
    e.dataTransfer.effectAllowed = 'move'
    // Add a custom drag image or style
    const target = e.currentTarget as HTMLDivElement
    target.style.opacity = '0.5'
  }, [isReorderMode])

  const handleDragEnd = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isReorderMode) return
    const target = e.currentTarget as HTMLDivElement
    target.style.opacity = '1'
    setDraggedItem(null)
    setDraggedOverId(null)
    dragCounter.current = 0
  }, [isReorderMode])

  const handleDragEnter = useCallback((e: React.DragEvent<HTMLDivElement>, id: string) => {
    if (!isReorderMode || !draggedItem) return
    e.preventDefault()
    dragCounter.current++
    if (draggedItem.id !== id) {
      setDraggedOverId(id)
    }
  }, [isReorderMode, draggedItem])

  const handleDragLeave = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isReorderMode) return
    e.preventDefault()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setDraggedOverId(null)
    }
  }, [isReorderMode])

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    if (!isReorderMode) return
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
  }, [isReorderMode])

  const handleDrop = useCallback(async (e: React.DragEvent<HTMLDivElement>, targetItem: DataSource) => {
    if (!isReorderMode || !draggedItem) return
    e.preventDefault()
    dragCounter.current = 0
    
    if (draggedItem.id === targetItem.id) {
      setDraggedItem(null)
      setDraggedOverId(null)
      return
    }

    // Create new order based on the drop position
    const newConnections = [...connections]
    const draggedIndex = newConnections.findIndex(c => c.id === draggedItem.id)
    const targetIndex = newConnections.findIndex(c => c.id === targetItem.id)
    
    if (draggedIndex === -1 || targetIndex === -1) return
    
    // Remove dragged item and insert at new position
    const [removed] = newConnections.splice(draggedIndex, 1)
    newConnections.splice(targetIndex, 0, removed)
    
    // Create ordered IDs array
    const orderedIds = newConnections.map(c => c.id).filter((id): id is string => id !== undefined)
    
    try {
      await bulkReorderMutation.mutateAsync(orderedIds)
    } catch (error) {
      toast.error('Failed to reorder datasources')
      console.error('Reorder error:', error)
    }
    
    setDraggedItem(null)
    setDraggedOverId(null)
  }, [isReorderMode, draggedItem, connections, bulkReorderMutation])

  const toggleReorderMode = useCallback(() => {
    setIsReorderMode(prev => !prev)
    setDraggedItem(null)
    setDraggedOverId(null)
  }, [])

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <div className="text-red-500 mb-4">Failed to load connections</div>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {error.message || 'An error occurred while loading connections'}
          </p>
          <button
            type="button"
            onClick={handleReload}
            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Database Connections</h1>
            <div className="flex items-center space-x-2">
              <p className="text-gray-600 dark:text-gray-400">
                Manage your MySQL and PostgreSQL database connections
              </p>
              {activeConnection && (
                <div className="flex items-center space-x-2 px-3 py-1 bg-green-100 dark:bg-green-900 rounded-full">
                  <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                  <span className="text-sm text-green-700 dark:text-green-300">
                    Connected to {activeConnection.name}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center space-x-2">
            {connections.length > 1 && (
              <button type="button"
                onClick={toggleReorderMode}
                className={clsx(
                  "inline-flex items-center px-4 py-2 text-sm font-medium rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-offset-2 transition-all duration-150",
                  isReorderMode 
                    ? "text-white bg-green-600 hover:bg-green-700 focus:bg-green-700 focus:ring-green-500"
                    : "text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 focus:ring-gray-500"
                )}
                title="Toggle reorder mode"
              >
                <ArrowsUpDownIcon className="w-4 h-4 mr-2" />
                <span>{isReorderMode ? 'Done Reordering' : 'Reorder'}</span>
              </button>
            )}
            <button type="button"
              onClick={handleCreateConnection}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-150"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              <span>New Connection</span>
            </button>
          </div>
        </div>
        
        {/* Search Bar - Only show if 2+ datasources */}
        {shouldShowSearch && (
          <>
            <div className="mt-4 max-w-md">
              <div className="relative">
                <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={connectionsSearch}
                  placeholder="Search connections by name..."
                  className="w-full pl-10 pr-10 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
                  onChange={(e) => { handleSearchChange(e.target.value) }}
                />
                {connectionsSearch && (
                  <button
                    type="button"
                    onClick={handleClearSearch}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
                    title="Clear search"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Search Results Summary */}
            {connectionsSearch && (
              <div className="mt-3 text-sm text-gray-600 dark:text-gray-400">
                {connections.length === 0 ? (
                  <span>No connections found for "{connectionsSearch}"</span>
                ) : (
                  <span>
                    Found {connections.length} connection{connections.length !== 1 ? 's' : ''} 
                    {connections.length > 0 && ` matching "${connectionsSearch}"`}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {connections.length === 0 ? (
        <div className="text-center py-12">
          <div className="w-16 h-16 mx-auto bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center mb-4">
            {connectionsSearch ? (
              <MagnifyingGlassIcon className="w-8 h-8 text-gray-400" />
            ) : (
              <PlusIcon className="w-8 h-8 text-gray-400" />
            )}
          </div>
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {connectionsSearch ? 'No matching connections' : 'No connections yet'}
          </h3>
          <p className="text-gray-600 dark:text-gray-400 mb-4">
            {connectionsSearch 
              ? `No connections match "${connectionsSearch}". Try adjusting your search terms.`
              : 'Get started by creating your first database connection.'
            }
          </p>
          {connectionsSearch ? (
            <button type="button"
              onClick={handleClearSearch}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-all duration-150"
            >
              <XMarkIcon className="w-4 h-4 mr-2" />
              <span>Clear Search</span>
            </button>
          ) : (
            <button type="button"
              onClick={handleCreateConnection}
              className="inline-flex items-center px-5 py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:bg-blue-700 rounded-lg shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-150"
            >
              <PlusIcon className="w-4 h-4 mr-2" />
              <span>Create Connection</span>
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 auto-rows-fr">
            {connections.map((connection) => (
              <div
                key={connection.id}
                draggable={isReorderMode}
                onDragStart={(e) => { handleDragStart(e, connection) }}
                onDragEnd={handleDragEnd}
                onDragEnter={(e) => { handleDragEnter(e, connection.id!) }}
                onDragLeave={handleDragLeave}
                onDragOver={handleDragOver}
                onDrop={(e) => { void handleDrop(e, connection) }}
                className={clsx(
                  "relative transition-all duration-200",
                  isReorderMode && "cursor-move",
                  draggedOverId === connection.id && draggedItem?.id !== connection.id && "ring-2 ring-blue-500 ring-offset-2 rounded-lg scale-105",
                  draggedItem?.id === connection.id && "opacity-50"
                )}
              >
                {isReorderMode && (
                  <div className="absolute -top-2 -left-2 z-10 w-8 h-8 bg-blue-600 text-white rounded-full flex items-center justify-center text-sm font-bold shadow-lg">
                    {connections.findIndex(c => c.id === connection.id) + 1}
                  </div>
                )}
                <ConnectionCard
                  connection={connection}
                  onEdit={createEditHandler(connection)}
                  isReorderMode={isReorderMode}
                />
              </div>
            ))}
          </div>
          
          {/* Reorder mode notice */}
          {isReorderMode && (
            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
              <div className="flex items-center space-x-2 text-blue-700 dark:text-blue-300">
                <ArrowsUpDownIcon className="w-5 h-5" />
                <span className="text-sm font-medium">
                  Drag and drop connections to reorder them. Click "Done Reordering" when finished.
                </span>
              </div>
            </div>
          )}

          {/* Loading indicator for infinite scroll */}
          {isFetchingNextPage && !isReorderMode && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              <span className="ml-2 text-sm text-gray-600 dark:text-gray-400">
                Loading more connections...
              </span>
            </div>
          )}
          
          {/* End of results indicator */}
          {!hasNextPage && !isFetchingNextPage && connections.length > 12 && (
            <div className="text-center py-8">
              <p className="text-sm text-gray-500 dark:text-gray-400">
                You've reached the end of your connections ({connections.length} total)
              </p>
            </div>
          )}
        </>
      )}

      {showModal && (
        <ConnectionModal
          existingConnection={editingConnection ?? undefined}
          onClose={handleCloseModal}
        />
      )}
    </div>
  )
}
